import React, { useState } from 'react'
import { Trash2, MessageSquare, Maximize2, Sparkles, Image } from 'lucide-react'
import { Button, Modal } from '../common'
import { formatDateTime } from '../../utils/helpers'
import useCategoryStore from '../../stores/categoryStore'

// Get image URL (relative URLs work via Vite proxy in dev)
function getImageUrl(photo, useThumbnail = true) {
  if (useThumbnail) {
    return photo.thumbnailUrl || photo.imageUrl || photo.thumbnail || photo.imageData || ''
  }
  return photo.imageUrl || photo.imageData || ''
}

export default function PhotoCard({
  photo,
  onDelete,
  onUpdateNotes,
  onToggleAnalyse,
  showCategory = true
}) {
  const [showFullSize, setShowFullSize] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [notes, setNotes] = useState(photo.notes || '')
  const [deleting, setDeleting] = useState(false)

  const getCategory = useCategoryStore(state => state.getCategory)
  const category = getCategory(photo.categoryCode)

  // Default to true if not set
  const analyseWithAI = photo.analyseWithAI !== false

  const handleDelete = async () => {
    if (deleting) return
    setDeleting(true)
    try {
      await onDelete(photo.id)
    } catch (error) {
      console.error('Failed to delete photo:', error)
      setDeleting(false)
    }
  }

  const handleSaveNotes = async () => {
    try {
      await onUpdateNotes(photo.id, notes)
      setShowNotesModal(false)
    } catch (error) {
      console.error('Failed to save notes:', error)
    }
  }

  const handleToggleAnalyse = async () => {
    if (onToggleAnalyse) {
      try {
        await onToggleAnalyse(photo.id, !analyseWithAI)
      } catch (error) {
        console.error('Failed to toggle analyse flag:', error)
      }
    }
  }

  return (
    <>
      <div className="group relative bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
        {/* Thumbnail */}
        <div
          className="aspect-square bg-slate-100 cursor-pointer relative"
          onClick={() => setShowFullSize(true)}
        >
          <img
            src={getImageUrl(photo)}
            alt={category?.name || 'Photo'}
            className="w-full h-full object-cover"
          />

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
            <Maximize2 className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>

          {/* Category badge */}
          {showCategory && category && (
            <div className="absolute top-2 left-2 px-2 py-1 bg-primary-700/90 text-white text-xs font-medium rounded">
              {category.code}
            </div>
          )}

          {/* Notes indicator */}
          {photo.notes && (
            <div className="absolute top-2 right-8 p-1.5 bg-accent-500 text-white rounded-full">
              <MessageSquare className="w-3 h-3" />
            </div>
          )}

          {/* AI Analysis indicator */}
          <div
            className={`absolute top-2 right-2 p-1.5 rounded-full ${
              analyseWithAI
                ? 'bg-blue-500 text-white'
                : 'bg-slate-300 text-slate-500'
            }`}
            title={analyseWithAI ? 'AI will analyse' : 'Reference only'}
          >
            {analyseWithAI ? (
              <Sparkles className="w-3 h-3" />
            ) : (
              <Image className="w-3 h-3" />
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between p-2 bg-slate-50">
          <span className="text-xs text-slate-500">
            {formatDateTime(photo.capturedAt)}
          </span>
          <div className="flex gap-1">
            {/* AI Toggle Button */}
            <button
              onClick={handleToggleAnalyse}
              className={`p-1.5 rounded transition-colors ${
                analyseWithAI
                  ? 'text-blue-500 bg-blue-50 hover:bg-blue-100'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
              title={analyseWithAI ? 'AI analysis ON - click to turn off' : 'AI analysis OFF - click to turn on'}
            >
              <Sparkles className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowNotesModal(true)}
              className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
              title="Add notes"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
              title="Delete photo"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Full size view modal */}
      <Modal
        isOpen={showFullSize}
        onClose={() => setShowFullSize(false)}
        size="xl"
        title={category ? `${category.code} - ${category.name}` : 'Photo'}
      >
        <div className="flex flex-col gap-4">
          <img
            src={getImageUrl(photo, false)}
            alt={category?.name || 'Photo'}
            className="w-full rounded-lg"
          />

          {/* AI Status in modal */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            analyseWithAI ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'
          }`}>
            {analyseWithAI ? (
              <>
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">AI will analyse this photo</span>
              </>
            ) : (
              <>
                <Image className="w-4 h-4" />
                <span className="text-sm font-medium">Reference photo only (no AI analysis)</span>
              </>
            )}
          </div>

          {photo.notes && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-1">Notes:</p>
              <p className="text-slate-600">{photo.notes}</p>
            </div>
          )}
          <p className="text-sm text-slate-500">
            Captured: {formatDateTime(photo.capturedAt)}
          </p>
        </div>
      </Modal>

      {/* Notes modal */}
      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title="Photo Notes"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowNotesModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveNotes}>
              Save Notes
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <img
            src={getImageUrl(photo)}
            alt="Preview"
            className="w-32 h-32 object-cover rounded-lg mx-auto"
          />
          <p className="text-xs text-center text-slate-500">
            Notes will be included in the AI analysis prompt for additional context.
          </p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this photo (e.g., 'damp patch in corner', 'crack above window')..."
            rows={4}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
          />
        </div>
      </Modal>
    </>
  )
}
